/**
 * Follow-cursor tooltip naming the all-day events behind a greyed day column.
 *
 * Same mechanism as `ScheduleInterview/calendar/MinimumNoticeHoverPopover` —
 * one listener plus `elementsFromPoint`, because the wash is a full-column
 * background region rather than a discrete control. Kept separate rather than
 * generalising that component: it is shipped, and its copy/icon/class are all
 * min-notice specific. Converging the two is a follow-up.
 *
 * A hook rather than a wrapper component so it attaches to each calendar's
 * EXISTING root — no extra `position: relative` box pushed into their flex
 * layouts, and no re-indenting their whole JSX tree to adopt it.
 *
 * Visually a tooltip, not a card: the same dark pill as every kula-ai
 * `Tooltip` in the calendar, so a wash explains itself without announcing
 * itself.
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box } from '@chakra-ui/react';
import { KText } from '@/ui-library/components';
import { pickAllDayTitlesFromStack } from './allDayBlockedOverlay';

/** Estimated card box, used only to decide which side of the cursor to flip to. */
const CARD_WIDTH = 240;
const CARD_HEIGHT = 72;
const CURSOR_OFFSET = 14;

interface HoverState {
  top: number;
  left: number;
  titles: string[];
}

export interface AllDayBlockedHoverTooltip {
  /** Put on the calendar's own root — the element the cursor moves across. */
  allDayHoverRef: React.RefObject<HTMLDivElement>;
  /** Render anywhere inside that root; it portals itself to `body`. */
  allDayHoverTooltip: React.ReactNode;
}

export function useAllDayBlockedHoverTooltip(): AllDayBlockedHoverTooltip {
  const hostRef = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState<HoverState | null>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const onMove = (e: MouseEvent) => {
      // Optional-called: not every DOM implementation ships it, and the
      // tooltip is an enhancement — losing it must never break the calendar.
      const stack = document.elementsFromPoint?.(e.clientX, e.clientY) ?? [];
      const titles = pickAllDayTitlesFromStack(stack);
      if (!titles?.length) {
        setHover((prev) => (prev === null ? prev : null));
        return;
      }
      // Flip to the other side of the cursor near a viewport edge so the card
      // never gets clipped by the drawer or the window.
      const left =
        window.innerWidth - e.clientX > CARD_WIDTH + CURSOR_OFFSET
          ? e.clientX + CURSOR_OFFSET
          : e.clientX - CARD_WIDTH - CURSOR_OFFSET;
      const top =
        window.innerHeight - e.clientY > CARD_HEIGHT + CURSOR_OFFSET
          ? e.clientY + CURSOR_OFFSET
          : e.clientY - CARD_HEIGHT - CURSOR_OFFSET;
      setHover({ top, left, titles });
    };
    const onLeave = () => setHover(null);

    host.addEventListener('mousemove', onMove);
    host.addEventListener('mouseleave', onLeave);
    return () => {
      host.removeEventListener('mousemove', onMove);
      host.removeEventListener('mouseleave', onLeave);
    };
  }, []);

  // Portalled into `body` so the `fixed` coordinates resolve against the
  // viewport — the wizard mounts inside a transformed modal wrapper, which
  // would otherwise become the containing block and offset the card from the
  // cursor. Same reasoning as `MinimumNoticeHoverPopover`.
  const card = hover ? (
    <Box
      data-test-id="all-day-blocked-tooltip"
      position="fixed"
      // Inline, not Chakra props: these change on every mousemove, and Chakra
      // would compile each pixel value into its own emotion class.
      style={{ top: `${hover.top}px`, left: `${hover.left}px` }}
      maxW={`${CARD_WIDTH}px`}
      bg="secondary-950"
      borderRadius="6px"
      px="10px"
      py="6px"
      zIndex={999999}
      pointerEvents="none"
      boxShadow="0px 4px 10px rgba(215, 218, 235, 0.1), 0px 18px 18px rgba(215, 218, 235, 0.09)"
    >
      {/* The calendar's own words only — product asked for the event's
          details on hover, not a generic label above them. */}
      {hover.titles.map((title, i) => (
        <KText
          key={`${title}-${i}`}
          as="div"
          color="base-white"
          size="sm"
          lineHeight="18px"
        >
          {title}
        </KText>
      ))}
    </Box>
  ) : null;

  return {
    allDayHoverRef: hostRef,
    allDayHoverTooltip: card ? createPortal(card, document.body) : null,
  };
}
